/**
 * Detects visual element types in LLM response text
 * Returns Set of detected types for metadata/badge display
 */
export type VisualElementType =
  | "markdown"
  | "table"
  | "code"
  | "image"
  | "diagram"
  | "math"
  | "html";

/**
 * Analyzes text content and identifies visual element types present
 * Used for metadata tagging and UI badge display
 *
 * @param text - Raw response text to analyze
 * @returns Set of detected visual element types
 */
export function detectVisualElements(text: string): Set<VisualElementType> {
  const detected = new Set<VisualElementType>();

  // Code fences (```language or ~~~)
  if (/```[\s\S]*?```|~~~[\s\S]*?~~~/m.test(text)) {
    detected.add("code");
  }

  // Tables (markdown pipe syntax with header separator)
  if (/^\|.+\|$/m.test(text) && /^\|[\s:|-]+\|$/m.test(text)) {
    detected.add("table");
  }

  // Images (![alt](url) or <img>)
  if (/!\[.*?\]\(.*?\)|<img[^>]+>/i.test(text)) {
    detected.add("image");
  }

  // Diagrams (mermaid, graphviz, plantuml blocks)
  if (/```(mermaid|dot|plantuml|graphviz)/i.test(text)) {
    detected.add("diagram");
  }

  // Math notation ($...$ or $$...$$)
  if (/\$\$[\s\S]+?\$\$|\$[^$\n]+?\$/m.test(text)) {
    detected.add("math");
  }

  // HTML tags (excluding safe inline like <br>)
  if (/<(div|span|p|section|article|aside|header|footer|nav|ul|ol|li|dl|dt|dd|table|tr|td|th|form|button|input|select|textarea|iframe|script|style)[^>]*>/i.test(text)) {
    detected.add("html");
  }

  // Markdown formatting (bold, italic, headings, links, lists)
  if (/(^|\n)#{1,6}\s|\*\*[^*]+\*\*|__[^_]+__|_[^_]+_|\*[^*]+\*|\[.+?\]\(.+?\)|^[-*+]\s|^\d+\.\s/m.test(text)) {
    detected.add("markdown");
  }

  return detected;
}
